import { CheckIcon, DownloadIcon, KeyRoundIcon } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { CopyButton } from '@/components/CopyButton';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  emailValidator,
  oneOfValidator,
  phoneNormalizer,
  phoneValidator,
  type ImportSchema,
} from '@/components/workbook/import-schema';
import {
  WorkbookImporter,
  type ImportPayload,
  type ServerValidation,
} from '@/components/workbook/WorkbookImporter';
import { api, errorMessage } from '@/lib/api';
import type { OrgImportResult } from '@/lib/types';
import { useImportUserRows, useTenantSettings } from './queries';

const ROLE_OPTIONS = ['org_admin', 'admin', 'member', 'user'];
const STATUS_OPTIONS = ['active', 'disabled'];
const ACTION_OPTIONS = ['upsert', 'create', 'update', 'disable', 'enable', 'delete'];

/**
 * Users import = the generic WorkbookImporter with the user schema and a debounced server dry run
 * (existing users, unknown jobs, phone clashes). Users are matched by email; new users get the
 * password from the sheet or a generated temporary one that is shown once after the import.
 */
export function UsersImportDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const settings = useTenantSettings(open);
  const importRows = useImportUserRows();
  const [updateExisting, setUpdateExisting] = useState(true);
  const [replaceJobs, setReplaceJobs] = useState(false);
  const [result, setResult] = useState<OrgImportResult | null>(null);

  useEffect(() => {
    if (!open) {
      setUpdateExisting(true);
      setReplaceJobs(false);
      setResult(null);
    }
  }, [open]);

  const defaultCountry = settings.data?.settings.defaultCountry ?? 'US';

  const schema = useMemo<ImportSchema>(
    () => ({
      noun: 'user',
      fields: [
        {
          key: 'email',
          label: 'Email',
          required: true,
          unique: true,
          aliases: ['email address', 'mail', 'login', 'username', 'correo'],
          validate: emailValidator,
          normalize: (v) => v.trim().toLowerCase() || null,
        },
        {
          key: 'firstName',
          label: 'First name',
          aliases: ['first', 'given name', 'fname', 'nombre'],
        },
        {
          key: 'lastName',
          label: 'Last name',
          aliases: ['last', 'surname', 'family name', 'lname', 'apellido'],
        },
        {
          key: 'fullName',
          label: 'Full name',
          hint: 'Split into first/last automatically',
          aliases: ['name', 'user', 'person'],
        },
        {
          key: 'phone',
          label: 'Mobile number',
          aliases: ['phone number', 'mobile', 'cell', 'cell phone', 'telefono'],
          validate: phoneValidator(defaultCountry),
          normalize: phoneNormalizer(defaultCountry),
        },
        {
          key: 'role',
          label: 'Organization role',
          hint: 'org_admin or member',
          aliases: ['tenant role', 'org role', 'access level'],
          validate: oneOfValidator(ROLE_OPTIONS, 'role'),
        },
        {
          key: 'status',
          label: 'Status',
          hint: 'active or disabled',
          aliases: ['state'],
          validate: oneOfValidator(STATUS_OPTIONS, 'status'),
        },
        {
          key: 'jobs',
          label: 'Jobs',
          hint: '"Orlando Tower:admin; Tampa Bridge Retrofit" - role defaults to user',
          aliases: ['job', 'job access', 'assignments', 'assigned jobs', 'projects'],
        },
        {
          key: 'password',
          label: 'Password',
          hint: 'New users only; leave blank to generate one',
          aliases: ['temporary password', 'initial password'],
        },
        {
          key: 'action',
          label: 'Action',
          hint: 'upsert (default), disable, enable or delete',
          aliases: ['operation', 'op'],
          validate: oneOfValidator(ACTION_OPTIONS, 'action'),
        },
      ],
    }),
    [defaultCountry],
  );

  const toInput = useCallback(
    (p: ImportPayload, dryRun: boolean) => ({
      fileName: p.fileName,
      sheetName: p.sheetName,
      headers: p.headers,
      rows: p.rows,
      mapping: p.mapping,
      updateExisting,
      dryRun,
      membershipMode: replaceJobs ? ('replace' as const) : ('merge' as const),
    }),
    [updateExisting, replaceJobs],
  );

  const validateOnServer = useCallback(
    async (p: ImportPayload, signal: AbortSignal): Promise<ServerValidation> => {
      const r = await api.users.importRows(toInput(p, true), signal);
      return {
        rows: r.rowResults.map((x) => ({
          index: x.index,
          status: x.status,
          reason: x.reason,
          field: x.field,
        })),
        summary: [
          { label: 'new', value: r.created, tone: 'success' },
          { label: 'updated', value: r.updated, tone: 'secondary' },
          ...(r.deactivated > 0
            ? [{ label: 'disabled', value: r.deactivated, tone: 'warning' as const }]
            : []),
          ...(r.reactivated > 0
            ? [{ label: 're-enabled', value: r.reactivated, tone: 'secondary' as const }]
            : []),
          { label: 'skipped', value: r.skipped, tone: 'muted' },
          ...(r.errorCount > 0
            ? [{ label: 'errors', value: r.errorCount, tone: 'danger' as const }]
            : []),
        ],
        note:
          r.created > 0 ? (
            <p className="text-muted-foreground text-xs">
              New users without a Password cell get a temporary password, shown once after the
              import.
            </p>
          ) : undefined,
      };
    },
    [toInput],
  );

  const onImport = useCallback(
    async (p: ImportPayload) => {
      try {
        const r = await importRows.mutateAsync(toInput(p, false));
        setResult(r);
        toast.success(`Imported ${r.created} new and updated ${r.updated} users`);
      } catch (err) {
        toast.error(errorMessage(err));
      }
    },
    [importRows, toInput],
  );

  const credentialsText = result
    ? result.credentials.map((c) => `${c.email}\t${c.temporaryPassword}`).join('\n')
    : '';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[92vh] flex-col overflow-hidden sm:max-w-[min(96vw,1240px)]">
        <DialogHeader>
          <DialogTitle>Import users</DialogTitle>
          <DialogDescription>
            Upload an Excel workbook or CSV, check the preview, fix anything highlighted, then
            import. Users are matched by email; blank cells leave existing values alone. The Jobs
            column assigns people to jobs ("Job name:admin"), and Status / Action disable or
            re-enable accounts.
          </DialogDescription>
        </DialogHeader>

        {result ? (
          <div className="grid gap-5">
            <div className="flex items-center gap-3 border p-4">
              <CheckIcon className="text-success size-5" />
              <div className="grid gap-0.5 text-sm">
                <span className="font-medium">Import complete</span>
                <span className="text-muted-foreground text-xs">
                  {result.created} new · {result.updated} updated · {result.skipped} skipped ·{' '}
                  {result.errorCount} errors
                  {result.deactivated > 0 && ` · ${result.deactivated} disabled`}
                  {result.reactivated > 0 && ` · ${result.reactivated} re-enabled`}
                </span>
              </div>
            </div>
            {result.credentials.length > 0 && (
              <div className="grid gap-3 border p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="grid gap-0.5">
                    <span className="flex items-center gap-2 text-sm font-medium">
                      <KeyRoundIcon className="size-4" /> Temporary passwords
                    </span>
                    <span className="text-muted-foreground text-xs">
                      Shown only once. Share them securely; each person should change their password
                      after signing in.
                    </span>
                  </div>
                  <CopyButton value={credentialsText} label="Copy all" />
                </div>
                <ul className="grid gap-1 font-mono text-xs">
                  {result.credentials.map((c) => (
                    <li
                      key={c.email}
                      className="flex items-center justify-between gap-3 border-b py-1 last:border-b-0"
                    >
                      <span>{c.email}</span>
                      <span className="flex items-center gap-2">
                        <span>{c.temporaryPassword}</span>
                        <CopyButton value={c.temporaryPassword} />
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {result.errors.length > 0 && (
              <ul className="text-muted-foreground max-h-48 overflow-y-auto border p-3 text-xs">
                {result.errors.map((e) => (
                  <li key={`${e.row}-${e.reason}`}>
                    Row {e.row}: {e.reason}
                  </li>
                ))}
              </ul>
            )}
            <DialogFooter>
              <Button type="button" onClick={() => onOpenChange(false)}>
                Done
              </Button>
            </DialogFooter>
          </div>
        ) : (
          <WorkbookImporter
            schema={schema}
            validateOnServer={validateOnServer}
            onImport={onImport}
            validationKey={`${updateExisting}|${replaceJobs}`}
            importLabel={({ rows, errors }) =>
              `Import ${Math.max(0, rows - errors).toLocaleString()} users`
            }
            pickerExtras={
              <>
                <span className="text-muted-foreground text-xs">Need a starting point?</span>
                <div className="flex flex-wrap gap-2">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() =>
                      void api.users
                        .templateXlsx()
                        .catch((e: unknown) => toast.error(errorMessage(e)))
                    }
                  >
                    <DownloadIcon /> Excel template
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() =>
                      void api.users.template().catch((e: unknown) => toast.error(errorMessage(e)))
                    }
                  >
                    <DownloadIcon /> CSV template
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() =>
                      void api.users
                        .exportXlsx()
                        .catch((e: unknown) => toast.error(errorMessage(e)))
                    }
                  >
                    <DownloadIcon /> Export current users
                  </Button>
                </div>
              </>
            }
            options={
              <div className="grid gap-4 border bg-card p-3">
                <h4 className="text-xs font-semibold tracking-wide uppercase text-muted-foreground">
                  Options
                </h4>
                <label className="flex items-start gap-3">
                  <Checkbox
                    checked={updateExisting}
                    onCheckedChange={(v) => setUpdateExisting(Boolean(v))}
                    className="mt-0.5"
                  />
                  <span className="grid gap-0.5">
                    <span className="text-sm font-medium">Update existing users</span>
                    <span className="text-muted-foreground text-xs">
                      When the email already exists, apply the filled-in cells instead of skipping
                      the row.
                    </span>
                  </span>
                </label>
                <label className="flex items-start gap-3">
                  <Checkbox
                    checked={replaceJobs}
                    onCheckedChange={(v) => setReplaceJobs(Boolean(v))}
                    className="mt-0.5"
                  />
                  <span className="grid gap-0.5">
                    <span className="text-sm font-medium">Replace job assignments</span>
                    <span className="text-muted-foreground text-xs">
                      Remove a person from jobs that are not in their Jobs cell. Off = only add or
                      change the listed jobs.
                    </span>
                  </span>
                </label>
              </div>
            }
          />
        )}
      </DialogContent>
    </Dialog>
  );
}
